import { UI, HStack, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'native-base';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { NativeSyntheticEvent, TextInputChangeEventData } from 'react-native';
import { useRecoilValue } from 'recoil';

import { customerDetailsEmail } from '../../../../../../../../store/customerDetail/privatePension/atom';

import {
  getDefaultValues,
  getValidationCreateSchema,
  PrivatePensionApplicationFormData,
} from './constants';

export interface PrivatePensionApplicationFormProps {
  handleConfirmApplication: (data: ReturnType<typeof getDefaultValues>) => void;
  handleReturn: () => void;
  isExecuting: boolean;
}

export const PrivatePensionApplicationForm: React.FC<PrivatePensionApplicationFormProps> = ({
  isExecuting,
  handleReturn,
  handleConfirmApplication,
}) => {
  const clientEmail = useRecoilValue(customerDetailsEmail);
  const minValue = 100;

  const [applicationState, setApplicationState] = useState(getDefaultValues(minValue));

  const {
    control,
    setValue,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<PrivatePensionApplicationFormData>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      value: Utils.Mask.Money(minValue),
      clientEmail: clientEmail ?? '',
      advisorEmail: '',
    },
    resolver: zodResolver(getValidationCreateSchema(minValue, 50000)),
  });

  const handleConfirm = handleSubmit(() => handleConfirmApplication(applicationState));

  const handleValueChange = useCallback(
    (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
      setApplicationState((state) => ({ ...state, value: Utils.Unmask.Money(e.nativeEvent.text) }));
    },
    [setApplicationState]
  );

  const handleClientEmailChange = useCallback(
    (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
      setApplicationState((state) => ({ ...state, clientEmail: e.nativeEvent.text }));
    },
    [setApplicationState]
  );

  const handleAdvisorEmailChange = useCallback(
    (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
      setApplicationState((state) => ({ ...state, advisorEmail: e.nativeEvent.text }));
    },
    [setApplicationState]
  );

  useEffect(() => {
    if (clientEmail) {
      setValue('clientEmail', clientEmail, { shouldValidate: true });
      setApplicationState((state) => ({ ...state, clientEmail }));
    }
  }, [clientEmail, setValue]);

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <UI.Text variant="semibold" color="neutral.black">
        Ação necessária
      </UI.Text>
      <VStack space={Spacing['stack.quarck']} w="full">
        <UI.Text color="neutral.black" variant="semibold">
          E-mail do cliente
        </UI.Text>

        <UI.Input
          placeholder="Digite o email do cliente"
          control={control}
          name="clientEmail"
          autoComplete="off"
          value={applicationState.clientEmail}
          onChange={handleClientEmailChange}
          variant="filled"
          hasShadow={false}
          error={errors.clientEmail}
        />
      </VStack>

      <VStack space={Spacing['stack.quarck']} w="full">
        <UI.Text color="neutral.black" variant="semibold">
          E-mail do assessor (opcional)
        </UI.Text>

        <UI.Input
          placeholder="Digite o email do assessor"
          control={control}
          name="advisorEmail"
          autoComplete="off"
          value={applicationState.advisorEmail}
          onChange={handleAdvisorEmailChange}
          variant="filled"
          hasShadow={false}
          error={errors.advisorEmail}
        />
      </VStack>

      <VStack space={Spacing['stack.quarck']} w="full">
        <UI.Text color="neutral.black" variant="semibold">
          Valor do aporte
        </UI.Text>

        <UI.Input
          placeholder="Digite o valor do aporte"
          control={control}
          name="value"
          autoComplete="off"
          value={applicationState.value.toString()}
          keyboardType="numeric"
          mask={Utils.Mask.InputMoney}
          onChange={handleValueChange}
          variant="filled"
          hasShadow={false}
          error={errors.value}
        />
      </VStack>

      <UI.Text color="neutral.dark">
        Atenção: O boleto será registrado e encaminhado para o(s) e-mail(s) informado(s) no próximo
        dia útil. Pedimos que aguarde até o fim do dia e caso não receba, entre em contato com a
        área de Previdência.
      </UI.Text>

      <HStack
        space={Spacing['inline.xxxs']}
        w={Utils.Responsive.DesktopPhone({ phone: 'full', desktop: 'auto' })}
        justifyContent="space-between">
        <UI.Button
          variant="secondary"
          onPress={handleReturn}
          w={Utils.Responsive.DesktopPhone({ phone: '45%', desktop: 'auto' })}>
          Voltar
        </UI.Button>
        <Button
          variant={'primary'}
          onPress={handleConfirm}
          isDisabled={!isValid}
          isLoadingText="Gerando..."
          spinnerPlacement="end"
          isLoading={isExecuting}
          w={Utils.Responsive.DesktopPhone({ phone: '45%', desktop: 'auto' })}>
          Gerar boleto
        </Button>
      </HStack>
    </VStack>
  );
};
